import { MantineProvider, ColorSchemeProvider } from "@mantine/core";
import { useColorScheme, useLocalStorage } from "@mantine/hooks";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { ColorSchemeModeContext } from "./components/ColorSchemeMode";

// Superseded by "color-scheme-mode" below - only read once, to migrate anyone who'd already made
// an explicit choice via the old nav-drawer toggle, then removed.
const LEGACY_COLOR_SCHEME_KEY = "color-scheme";

// Three explicit modes, chosen from Profile > Appearance, rather than just a resolved light/dark
// value: "device" continuously mirrors the OS/browser's own prefers-color-scheme setting -
// including a live change (e.g. a scheduled night mode) while the tab stays open, not just
// whatever it happened to be on the last page load - and is the default for anyone who hasn't
// chosen otherwise. Picking "light" or "dark" explicitly overrides that and sticks across visits
// regardless of what the device does afterwards, and "device" itself stays reachable as an
// explicit option to go back to (rather than only being reachable by clearing storage).
function Root() {
  const preferredColorScheme = useColorScheme();
  const [mode, setMode] = useLocalStorage({
    key: "color-scheme-mode",
    defaultValue: "device",
    getInitialValueInEffect: true,
  });

  // One-time migration: carry over anyone's previously-saved explicit light/dark choice (from the
  // old 2-way nav-drawer toggle) into the new 3-way mode, rather than silently resetting everyone
  // back to "device" the first time they load the app after this change. Only runs while the new
  // key is still unset, so it can't re-fire and stomp on a deliberate later choice.
  useEffect(() => {
    const legacyValue = window.localStorage.getItem(LEGACY_COLOR_SCHEME_KEY);
    if (legacyValue && window.localStorage.getItem("color-scheme-mode") === null) {
      try {
        setMode(JSON.parse(legacyValue));
      } finally {
        window.localStorage.removeItem(LEGACY_COLOR_SCHEME_KEY);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const colorScheme = mode === "device" ? preferredColorScheme : mode;
  // Kept only so any Mantine-internal consumer of its own useMantineColorScheme() hook still gets
  // sensible behaviour - flips between the two resolved colors explicitly, matching Mantine's own
  // expected toggleColorScheme signature. Profile > Appearance uses setMode directly instead, since
  // it also needs to offer "device" as a third option this can't express.
  const toggleColorScheme = (value) => setMode(value || (colorScheme === "dark" ? "light" : "dark"));

  return (
    <ColorSchemeModeContext.Provider value={{ mode, setMode }}>
      <ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
        <MantineProvider
          withGlobalStyles
          withNormalizeCSS
          theme={{
            colorScheme,
            // Site-wide font (loaded from Google Fonts in public/index.html) - applies to every
            // Mantine component (buttons, inputs, headings, tables, etc.) throughout the app.
            fontFamily: "Kanit, sans-serif",
            headings: { fontFamily: "Kanit, sans-serif" },
          }}
        >
          <Notifications position="top-center" zIndex={2077} />
          <ModalsProvider>
            <ErrorBoundary>
              <App />
            </ErrorBoundary>
          </ModalsProvider>
        </MantineProvider>
      </ColorSchemeProvider>
    </ColorSchemeModeContext.Provider>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(<Root />);

// Registers the service worker (production builds only - see serviceWorkerRegistration.js) so the
// app can be installed to a home screen/dock and its static shell loads instantly on repeat
// visits. When a new deploy has finished downloading in the background, the new worker used to sit
// there doing nothing until someone clicked "Refresh now" in a notification - which meant a plain
// browser refresh (or navigating away and back) kept serving the OLD cached version, since the new
// one was never actually told to take over. Sending SKIP_WAITING as soon as the update is detected
// fixes that: the new worker activates and takes control of this tab in the background right away,
// so ANY reload from this point on - a normal refresh, or just reopening the tab later - picks up
// the new version automatically. "Remove the update banner, apply changes straight away when
// refreshing the page" from the original request - there's nothing left for anyone to click at
// all now; a plain refresh is all it takes. This doesn't touch anything already loaded in the
// current tab, so nobody mid-way through booking a desk or filling in a form gets interrupted
// until they (or a later visit) actually reload.
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    registration.waiting?.postMessage({ type: "SKIP_WAITING" });
  },
});
